import React, { useEffect, useRef } from 'react';
import './styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Turnkey Design Content) ---
import {
    // Core Concepts & General
    faKey,              // Turnkey concept
    faBuilding,         // Architecture/Facility
    faUserCheck,        // Expertise/Trust
    faCog,              // Custom Solutions / Integration (Can also use faTools)
    faGlobe,            // Global Practices
    faLeaf,             // Sustainability
    faLaptopCode,       // Technology Integration / Future-Ready
    // Services
    faDraftingCompass,  // Architecture Design
    faCogs,             // MEP Design
    faRulerCombined,    // Structural Design
    faPalette,          // Interior Design
    faTree,             // Landscape Design
    faDirections,       // Wayfinding/Signage
    // Benefits / Importance
    faShieldAlt,        // Reduced Risk
    faDollarSign,       // Cost-Effective
    faRocket,           // Future-Proofing
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faSitemap, faFireExtinguisher, faHeartPulse, faCheckCircle, faMapMarkedAlt,
    faUsers, faLightbulb, faProjectDiagram, faHardHat, faUserTie, faUserCog,
    faUserShield, faGlobeAsia
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons

// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // What is Turnkey?
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Choose Cliniqor?
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Turnkey Services
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Importance of Turnkey

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for turnkey/construction/design
    const heroBgImage = 'https://images.unsplash.com/photo-1581092917219-05f80e6091ba?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Image showing construction/planning phase

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Turnkey Architecture Design for Healthcare Facilities</h1>
            <p className="cliniqor-sub-heading">
              End-to-End Solutions by Cliniqor for Planning, Design, and Construction
            </p>
            <p className="cliniqor-intro-text">
              Cliniqor delivers seamless, holistic turnkey services, encompassing conceptual design to final commissioning. We ensure state-of-the-art hospitals meeting present and future needs with precision, expertise, and deep industry knowledge.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- WHAT IS TURNKEY? --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>What is Turnkey Architecture Design for Healthcare Facilities?</h2>
            </div>
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <p>A comprehensive approach where Cliniqor takes full responsibility for design, planning, and execution. This all-in-one solution ensures a streamlined process with a single point of contact from concept to handover.</p>
                     <p>It eliminates coordination complexities, providing peace of mind that the project will be delivered on time, within budget, and to the highest standards of quality and functionality.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- WHY CHOOSE CLINIQOR? (Features Section) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Why Choose Cliniqor for Your Turnkey Hospital Design?</h2>
              <p>A trusted partner creating exceptional healthcare spaces.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained */}
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserCheck} /></div>
                    <h4>1. Deep Healthcare Expertise</h4>
                    <p>Beyond architecture, we understand unique healthcare challenges and workflows.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCog} /></div> {/* Cog for Custom */}
                    <h4>2. Custom Solutions</h4>
                    <p>Tailored designs for multi-specialty hospitals, centers, or specialized facilities.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faGlobe} /></div>
                    <h4>3. Integration of Global Best Practices</h4>
                    <p>Designs meet international standards while ensuring local compliance.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLeaf} /></div>
                    <h4>4. Sustainability & Eco-Friendly Design</h4>
                    <p>Incorporating green practices for minimal environmental impact and reduced costs.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLaptopCode} /></div>
                    <h4>5. Future-Ready Designs</h4>
                    <p>Integrating latest technologies (telemedicine, AI) for long-term adaptability.</p>
                </div>
              </div>
              {/* Grid might need adjusting if less than 6 items, or add an empty card */}
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- TURNKEY SERVICES (Detailed Planning Section - adapted for 6 items) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Our Turnkey Hospital Design Services</h2>
               <p>Covering every aspect from initial consultation to post-construction support.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for Services */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faDraftingCompass} /></div>
                    <div className="cliniqor-content"><h4>1. Comprehensive Architecture Design</h4> <p>Functional, efficient, patient-centered spaces optimizing workflows and future expansion.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faCogs} /></div>
                    <div className="cliniqor-content"><h4>2. MEP Design for Seamless Operations</h4> <p>State-of-the-art HVAC, sustainable energy, lighting, and plumbing solutions.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faRulerCombined} /></div>
                    <div className="cliniqor-content"><h4>3. Structural Design: Resilient & Safe</h4> <p>Robust structures meeting safety standards, considering traffic, demands, and seismic activity.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faPalette} /></div>
                    <div className="cliniqor-content"><h4>4. Interior Design: Healing Environments</h4> <p>Aesthetic, functional spaces enhancing patient experience and staff efficiency.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faTree} /></div>
                    <div className="cliniqor-content"><h4>5. Landscape Design: Beyond Walls</h4> <p>Healing gardens and functional outdoor spaces promoting well-being and recovery.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faDirections} /></div>
                    <div className="cliniqor-content"><h4>6. Wayfinding & Signage</h4> <p>Comprehensive, intuitive systems ensuring seamless navigation and ADA compliance.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- IMPORTANCE OF TURNKEY (Reusing Features Layout) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>The Importance of Turnkey Hospital Design Today</h2>
                 <p>Unparalleled benefits in a complex and evolving healthcare landscape.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Importance/Benefits --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faShieldAlt} /></div>
                            <h4>Reduced Risk</h4>
                            <p>Single-source accountability ensures projects stay on track and on budget.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDollarSign} /></div>
                            <h4>Cost-Effective Solutions</h4>
                            <p>Minimizes costs by eliminating multiple contractor coordination needs.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faRocket} /></div>
                            <h4>Future-Proofing Infrastructure</h4>
                            <p>Incorporating tech, flexibility, and sustainability for future adaptability.</p>
                        </div>
                    </div>
                    {/* Grid ideally suited for 3 items. Add empty placeholders or adjust CSS if needed */}
               </div>
           </div>
        </section>

       {/* Removed previous sections like Key Considerations, Team, Process, Nationwide */}

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Transforming Healthcare Infrastructure with Cliniqor</h3>
          <p>Create a state-of-the-art facility equipped for today's demands and future trends. Our comprehensive, patient-centered turnkey approach ensures excellence in patient care and hospital operations. Contact us today to start your journey.</p>
          <a href="#contact" className="cliniqor-cta-button">Discuss Your Turnkey Project</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;